import './HowDrawings.css';

// The drawings of the "how it works" page, and the small ones on the
// homepage: line drawings, a little wobbly, on a wash of colour - as if drawn
// with a pen. Nothing moves. All decoration (aria-hidden): the words next to
// them carry the meaning.

// The wobble of the pen and the bleed of the wash. Defined once
// per page, used by id.
export function HowDefs() {
  return (
    <svg className="hd-defs" aria-hidden="true" focusable="false">
      <filter id="hd-pen" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" seed="2" />
        <feDisplacementMap in="SourceGraphic" scale="2.6" />
      </filter>
      <filter id="hd-wash" x="-20%" y="-30%" width="140%" height="160%">
        <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="3" seed="11" />
        <feDisplacementMap in="SourceGraphic" scale="22" />
      </filter>
    </svg>
  );
}

function Art({ viewBox, children }) {
  return (
    <svg className="hd-svg" viewBox={viewBox} aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The pen. Everything inside <Pen> is drawn with a wobbly line; <Wash> lays a
// pool of colour underneath.
// ---------------------------------------------------------------------------

export const Pen = ({ children }) => <g filter="url(#hd-pen)">{children}</g>;

export const Wash = ({ cx, cy, rx, ry }) => (
  <ellipse className="hl-wash" cx={cx} cy={cy} rx={rx} ry={ry} filter="url(#hd-wash)" />
);

// A rectangle the way a hand draws one: four strokes, each running a little
// past the corner.
export const sketch = (x, y, w, h, o = 2) =>
  `M${x - o} ${y + 0.6} L${x + w + o} ${y - 0.6}` +
  ` M${x + w - 0.5} ${y - o} L${x + w + 0.6} ${y + h + o}` +
  ` M${x + w + o} ${y + h + 0.4} L${x - o} ${y + h - 0.5}` +
  ` M${x + 0.5} ${y + h + o} L${x - 0.6} ${y - o}`;

// A hand-drawn arrow: any curve, and an open head at its end, pointing
// `angle` degrees (0 = right, 90 = down).
export function HandArrow({ d, to: [x, y], angle, dashed }) {
  const arm = (turn) => {
    const a = ((angle + 180 + turn) * Math.PI) / 180;
    return `${(x + 10 * Math.cos(a)).toFixed(1)} ${(y + 10 * Math.sin(a)).toFixed(1)}`;
  };
  return (
    <>
      <path className={`hl ${dashed ? 'hl-dashed' : ''}`} d={d} />
      <path className="hl" d={`M${arm(-28)} L${x} ${y} L${arm(28)}`} />
    </>
  );
}

// The board, drawn: a frame with its grid of spots.
function PenBoard({ x, y, w, h, cols = 3, rows = 3 }) {
  const lines = [];
  for (let i = 1; i < cols; i += 1) lines.push(`M${x + (w * i) / cols} ${y + 3} V${y + h - 3}`);
  for (let i = 1; i < rows; i += 1) lines.push(`M${x + 3} ${y + (h * i) / rows} H${x + w - 3}`);
  return (
    <>
      <rect className="hl-paper" x={x} y={y} width={w} height={h} />
      <path className="hl hl-thin" d={lines.join(' ')} />
      <path className="hl" d={sketch(x, y, w, h)} />
    </>
  );
}

// One small canvas, blank or with something made on it.
export function PenCanvas({ x, y, s, rot = 0, mark }) {
  const c = s / 2;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot} ${c} ${c})`}>
      <rect className="hl-paper" width={s} height={s} />
      {mark === 'dot' && <circle className="hl-ink" cx={c} cy={c} r={s * 0.22} />}
      {mark === 'wave' && (
        <path
          className="hl hl-paint"
          d={`M${s * 0.18} ${s * 0.66} C${s * 0.34} ${s * 0.2}, ${s * 0.56} ${s * 0.86}, ${s * 0.82} ${s * 0.34}`}
        />
      )}
      {mark === 'hill' && <path className="hl-tint" d={`M${s * 0.1} ${s * 0.9} L${s * 0.9} ${s * 0.9} L${s * 0.9} ${s * 0.18} Z`} />}
      {mark === 'fill' && <rect className="hl-tint" x={s * 0.14} y={s * 0.14} width={s * 0.72} height={s * 0.72} />}
      {mark === 'lines' && (
        <path className="hl hl-thin" d={`M${s * 0.2} ${s * 0.3} H${s * 0.8} M${s * 0.2} ${s * 0.5} H${s * 0.7} M${s * 0.2} ${s * 0.7} H${s * 0.76}`} />
      )}
      {mark === 'heart' && (
        <path
          className="hl-ink"
          d={`M${c} ${s * 0.78} C${s * 0.16} ${s * 0.5}, ${s * 0.2} ${s * 0.2}, ${c} ${s * 0.38} C${s * 0.8} ${s * 0.2}, ${s * 0.84} ${s * 0.5}, ${c} ${s * 0.78} Z`}
        />
      )}
      {mark === 'circle' && <circle className="hl hl-paint" cx={c} cy={c} r={s * 0.24} />}
      {mark === 'cross' && <path className="hl hl-paint" d={`M${s * 0.3} ${s * 0.3} L${s * 0.7} ${s * 0.7} M${s * 0.7} ${s * 0.3} L${s * 0.3} ${s * 0.7}`} />}
      {mark === 'sun' && (
        <>
          <circle className="hl-tint" cx={c} cy={c} r={s * 0.17} />
          <path
            className="hl hl-thin"
            d={`M${c} ${s * 0.12} V${s * 0.24} M${c} ${s * 0.76} V${s * 0.88} M${s * 0.12} ${c} H${s * 0.24} M${s * 0.76} ${c} H${s * 0.88}`}
          />
        </>
      )}
      <path className="hl" d={sketch(0, 0, s, s, 1.5)} />
    </g>
  );
}

// A few blank canvases in a loose pile.
const Pile = ({ x, y, s = 22 }) => (
  <>
    <PenCanvas x={x} y={y} s={s} rot={-5} />
    <PenCanvas x={x + s * 0.3} y={y - s * 0.3} s={s} rot={7} />
    <PenCanvas x={x + s * 0.55} y={y + s * 0.1} s={s} rot={-12} />
  </>
);

// What we send: the board, with the blank canvases leaning against it.
const Bundle = ({ x, y }) => (
  <>
    <PenBoard x={x} y={y} w={60} h={60} />
    <Pile x={x + 38} y={y + 40} />
  </>
);

// A house, 60 wide and 64 tall from (x, y), smoke and all.
function House({ x, y, scale = 1, smoke = true }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path className="hl-paper" d="M0 26 L30 0 L60 26 V64 H0 Z" />
      <path className="hl" d="M-5 28 L30 -2 L65 28 M4 24 V64 M56 24 V64 M-4 64.5 H64" />
      <path className="hl" d="M24 64 V46 H36 V64" />
      <path className="hl hl-thin" d={`${sketch(41, 33, 9, 9, 1)} M45.5 33 V42 M41 37.5 H50`} />
      <path className="hl" d="M44 12 V2 H51 V18" />
      {smoke && <path className="hl hl-thin" d="M47.5 -4 C43 -9, 52 -12, 48 -18 S52 -24, 50 -29" />}
    </g>
  );
}

// Someone, from the shoulders up. The hair is what tells them apart.
const shoulders = (x, y) =>
  `M${x - 20} ${y + 52} C${x - 20} ${y + 26}, ${x - 12} ${y + 13}, ${x} ${y + 13}` +
  ` C${x + 12} ${y + 13}, ${x + 20} ${y + 26}, ${x + 20} ${y + 52}`;

export function Person({ x, y, hair, children }) {
  return (
    <>
      <path className="hl-paper" d={`${shoulders(x, y)} Z`} />
      <path className="hl" d={shoulders(x, y)} />
      <circle className="hl-paper" cx={x} cy={y} r="9" />
      <circle className="hl" cx={x} cy={y} r="9" />
      <circle className="hl-ink" cx={x - 3.2} cy={y - 0.5} r="1.1" />
      <circle className="hl-ink" cx={x + 3.2} cy={y - 0.5} r="1.1" />
      {hair === 'bun' && <circle className="hl" cx={x + 3} cy={y - 12} r="3.6" />}
      {hair === 'tuft' && <path className="hl" d={`M${x - 7} ${y - 5} C${x - 3} ${y - 13}, ${x + 4} ${y - 12}, ${x + 8} ${y - 5}`} />}
      {hair === 'long' && <path className="hl" d={`M${x - 8} ${y - 4} C${x - 12} ${y + 6}, ${x - 10} ${y + 12}, ${x - 7} ${y + 16} M${x + 8} ${y - 4} C${x + 12} ${y + 6}, ${x + 10} ${y + 12}, ${x + 7} ${y + 16}`} />}
      {hair === 'cap' && <path className="hl" d={`M${x - 9} ${y - 2} C${x - 9} ${y - 13}, ${x + 9} ${y - 13}, ${x + 9} ${y - 2} H${x + 15}`} />}
      {children}
    </>
  );
}

// ---------- The idea, in three steps ----------

// Choose: an empty board, its size marked, and the first canvas on its way.
export function StepChoose() {
  return (
    <Art viewBox="0 0 200 130">
      <Wash cx={100} cy={86} rx={88} ry={32} />
      <Pen>
        <path className="hl hl-thin" d="M36 40 V108 M32 40 H40 M32 108 H40 M52 120 H140 M52 116 V124 M140 116 V124" />
        <PenBoard x={52} y={40} w={88} h={68} cols={4} rows={3} />
        <HandArrow d="M156 36 C 146 30, 132 34, 128 50" to={[128, 50]} angle={100} dashed />
        <PenCanvas x={156} y={12} s={20} rot={14} />
      </Pen>
    </Art>
  );
}

// Make: a canvas on a little easel with something on it, the brush beside.
export function StepMake() {
  return (
    <Art viewBox="0 0 200 130">
      <Wash cx={100} cy={104} rx={82} ry={24} />
      <Pen>
        <path className="hl" d="M80 98 L66 126 M114 98 L128 126 M97 94 V126" />
        <path className="hl hl-thin" d="M44 126.5 C 90 125, 130 127, 158 126" />
        <rect className="hl-paper" x="58" y="24" width="76" height="74" />
        <path className="hl hl-paint hl-paint--big" d="M68 82 C 80 58, 94 96, 106 74 S 120 66, 124 70" />
        <circle className="hl-ink" cx="110" cy="44" r="8" />
        <path className="hl" d={sketch(58, 24, 76, 74)} />
        <g transform="translate(164 14) rotate(28)">
          <path className="hl hl-thick" d="M0 0 V50" />
          <rect className="hl-paper" x="-3.5" y="50" width="7" height="9" />
          <path className="hl hl-thin" d={sketch(-3.5, 50, 7, 9, 1)} />
          <path className="hl-ink" d="M-3.5 59 H3.5 C3.5 67, 1 72, 0 76 C-1 72, -3.5 67, -3.5 59 Z" />
        </g>
      </Pen>
    </Art>
  );
}

// Together: every spot filled, the board hung on one nail.
const TILE_MARKS = ['dot', 'wave', 'hill', 'lines', 'fill', 'dot', 'hill', 'wave', 'lines', 'fill', 'wave', 'dot'];
const TILE_TILT = [-3, 2, -2, 14, 3, -2, 2, -3, 1, -2, 3, -1];

export function StepTogether() {
  return (
    <Art viewBox="0 0 200 130">
      <Wash cx={100} cy={84} rx={86} ry={36} />
      <Pen>
        <path className="hl hl-thin" d="M56 44 L100 16 L144 44" />
        <circle className="hl-ink" cx="100" cy="15" r="2.6" />
        <rect className="hl-paper" x="50" y="40" width="100" height="78" />
        <path className="hl" d={sketch(50, 40, 100, 78)} />
        {TILE_MARKS.map((mark, i) => {
          const loose = i === 3;
          return (
            <PenCanvas
              key={i}
              x={55.25 + (i % 4) * 23.5 + (loose ? 3 : 0)}
              y={46 + Math.floor(i / 4) * 23.5 - (loose ? 4 : 0)}
              s={19}
              rot={TILE_TILT[i]}
              mark={mark}
            />
          );
        })}
      </Pen>
    </Art>
  );
}

// ---------- Who's who ----------

// The organiser: holding up the (still empty) board. `you` is the word for
// "you", written next to them with an arrow.
export function TheOrganiser({ you }) {
  return (
    <Art viewBox="0 0 260 140">
      <Wash cx={130} cy={92} rx={104} ry={40} />
      <Pen>
        <Person x={126} y={36} hair="tuft" />
        <PenBoard x={94} y={70} w={64} h={52} cols={4} rows={3} />
        <circle className="hl-paper" cx="92" cy="94" r="4" />
        <circle className="hl" cx="92" cy="94" r="4" />
        <circle className="hl-paper" cx="160" cy="94" r="4" />
        <circle className="hl" cx="160" cy="94" r="4" />
        <HandArrow d="M196 30 C 184 18, 164 18, 150 28" to={[150, 28]} angle={146} />
      </Pen>
      <text className="hl-word" x="202" y="36">
        {you}
      </text>
    </Art>
  );
}

// The makers: a row of people, each holding up the canvas they made.
const MAKERS = [
  [34, 46, 'long', 'dot', -6],
  [82, 38, 'cap', 'wave', 4],
  [130, 48, 'bun', 'hill', -3],
  [178, 40, 'tuft', 'lines', 7],
  [226, 46, undefined, 'dot', -5],
];

export function TheMakers() {
  return (
    <Art viewBox="0 0 260 140">
      <Wash cx={130} cy={98} rx={118} ry={36} />
      <Pen>
        {MAKERS.map(([x, y, hair, mark, rot]) => (
          <Person key={x} x={x} y={y} hair={hair}>
            <PenCanvas x={x - 12} y={y + 26} s={24} rot={rot} mark={mark} />
          </Person>
        ))}
      </Pen>
    </Art>
  );
}

// ---------- How it gets there ----------

// One parcel - the board and every canvas - to the organiser's door.
export function ShipToYou() {
  return (
    <Art viewBox="0 -20 260 150">
      <Wash cx={132} cy={92} rx={118} ry={30} />
      <Pen>
        <Bundle x={20} y={40} />
        <HandArrow d="M110 72 C 128 42, 156 40, 172 60" to={[172, 60]} angle={52} />
        <House x={186} y={40} />
        <path className="hl hl-thin" d="M8 105 C 70 103, 150 106, 252 104.5" />
      </Pen>
    </Art>
  );
}

// The board to the organiser; every canvas on its own to a maker's door.
export function ShipToMakers() {
  return (
    <Art viewBox="0 0 260 150">
      <Wash cx={136} cy={116} rx={120} ry={30} />
      <Pen>
        <PenBoard x={14} y={22} w={46} h={46} />
        <HandArrow d="M72 44 C 96 26, 126 26, 148 38" to={[148, 38]} angle={29} />
        <House x={160} y={18} scale={0.75} />

        <Pile x={14} y={112} s={18} />
        <HandArrow d="M56 122 C 72 112, 90 112, 106 122" to={[106, 122]} angle={32} />
        <HandArrow d="M54 112 C 80 92, 130 88, 156 106" to={[156, 106]} angle={36} />
        <HandArrow d="M52 104 C 96 70, 176 70, 204 102" to={[204, 102]} angle={50} />
        <House x={112} y={110} scale={0.5} smoke={false} />
        <House x={162} y={110} scale={0.5} smoke={false} />
        <House x={212} y={110} scale={0.5} smoke={false} />
        <path className="hl hl-thin" d="M100 142.5 C 150 141, 200 143, 254 142" />
      </Pen>
    </Art>
  );
}

// A postmark, stamped across the corner of a delivery option: where it all
// ends up, written round the ring, with the wavy cancellation lines.
export function Postmark({ id, place }) {
  return (
    <svg className="hd-postmark" viewBox="0 0 150 96" aria-hidden="true" focusable="false">
      <defs>
        <path id={id} d="M40.8 60.7 A30 30 0 1 1 95.2 60.7" />
      </defs>
      <Pen>
        <circle className="hl hl-thin" cx="68" cy="48" r="40" />
        <circle className="hl hl-thin" cx="68" cy="48" r="23" />
        <path className="hl hl-thin" d="M108 30 c8 -6, 14 6, 22 0 s14 6, 18 0 M110 48 c8 -6, 14 6, 22 0 s14 6, 16 0 M108 66 c8 -6, 14 6, 22 0 s14 6, 18 0" />
      </Pen>
      <text className="hd-postmark-word">
        <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
          {place}
        </textPath>
      </text>
      <path className="hl-ink" transform="translate(68 49) scale(0.62)" d="M-11 -11 h9 v9 h-9 Z M2 -11 h9 v9 h-9 Z M-11 2 h9 v9 h-9 Z M3 1 h9 v9 h-9 Z" />
    </svg>
  );
}

// A big, very light spot of watercolour for the page background: pools of
// the one blue, one inside another, with bled edges. Each `variant` has its
// own shape and its own bleed, so no two spots on a page look alike. It
// stretches to whatever box the page's CSS gives it. Purely decoration.
const SPOTS = {
  a: { seed: 21, turn: 0, pools: [[300, 220, 250, 170], [270, 236, 160, 104]] },
  b: { seed: 7, turn: -14, pools: [[310, 200, 236, 176], [348, 252, 138, 92], [176, 132, 72, 52]] },
  c: { seed: 33, turn: 16, pools: [[292, 232, 256, 148], [244, 196, 118, 108], [404, 300, 84, 62]] },
  d: { seed: 48, turn: -24, pools: [[280, 210, 230, 160], [380, 280, 120, 70], [170, 290, 60, 44]] },
  e: { seed: 59, turn: 9, pools: [[300, 230, 210, 190], [230, 170, 96, 80]] },
};

export function PaintSpot({ className, variant = 'a' }) {
  const { seed, turn, pools } = SPOTS[variant];
  const id = `hd-bleed-${variant}`;
  return (
    <svg className={`hd-spot ${className}`} viewBox="0 0 600 440" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <filter id={id} x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency="0.008" numOctaves="3" seed={seed} />
        <feDisplacementMap in="SourceGraphic" scale="70" />
      </filter>
      <g filter={`url(#${id})`}>
        <g transform={`rotate(${turn} 300 220)`}>
          {pools.map(([cx, cy, rx, ry]) => (
            <ellipse key={`${cx}-${cy}`} className="hd-spot-pool" cx={cx} cy={cy} rx={rx} ry={ry} />
          ))}
        </g>
      </g>
    </svg>
  );
}
